<!--
  공통 등급별 이벤트 그리드

  @since: 2018-08-27
  @author: mk.ko
-->

<template>
  <div class="grid-wrapper">
    <div class="page-contents-option-group option-group-sm" v-if="eventPopupCondition">
      <div class="page-contents-btn-group" v-if="!showPagination && tableName === 'eventGridTable'">
        <q-btn
          class="bs bs-q-btn-xs bs-q-btn-flat-type"
          color="dark"
          flat="flat"
          @click="onAlramSettingPopup"
        >
          <q-icon class="bs-q-btn-icon bs-q-btn-icon-advanced-settings"/>
          <span class="bs-q-btn-text">{{ $t('cmn.notification_setting') }}</span>
        </q-btn>
      </div>
      <div class="page-contents-btn-group" v-if="!disableSearchText">
        <q-search
          v-model="searchText"
          :placeholder="pageSearchTextPlaceholder ? $t('cmn.pageSearch') : $t('cmn.search')"
          class="bs bs-q-sm bs-q-search bs-q-search-default"
        />
      </div>
      <div class="page-contents-pagination-group">
        <div class="pagination-sum">{{ $t('cmn.pageCount', {count: totalPageCount}) }}</div>
        <q-pagination
          v-if="showPagination"
          v-model="pageIndex"
          :min="pageMin"
          :max="pageMax"
          :maxPages="maxPages"
          :boundaryLinks="void 0"
          :boundaryNumbers="true"
          :directionLinks="true"
          :ellipses="void 0"
          :input="true"
          @change="onInputPage"
          color="dark"
          type="select"
          class="bs bs-q-pagination bs-q-pagination-default"
        />
      </div>
    </div>
    <div class="contents-row" :style="`height: ${height}`">
      <q-resize-observable @resize="onVsResize"/>
      <device-status-data-table ref="dataTable"
                                :table-name="tableName"
                                row-key="event_log_id"
                                :data="filterTableData"
                                :columns="fields"
                                :height="getTableHeight"
                                :slim="slim"
                                :sortable="true"
                                :is-popup="isPopup"
                                @onRowDblClick="onRowDoubleClick"
                                @columnFilter="filter => setFilters(filter)"
      />
    </div>
  </div>
</template>

<script>
  // moment
  import Moment from 'moment'

  import { getColorLuminance } from '~/utils/charts'
  import { renderTooltip, destroyTooltip } from '~/utils/toolTipUtils'
  import { sortCharNum, ipSorter } from '~/utils/sort'

  import { createNamespacedHelpers } from 'vuex'
  import DeviceStatusDataTable from '~/components/vueTable/wrap/deviceStatusDataTable'
  const { mapState, mapActions } = createNamespacedHelpers('dashboard/widgetDetail')
  const deviceStatusHelper = createNamespacedHelpers('dashboard/deviceStatus')
  const { mapState: mapStateTopMenu } = createNamespacedHelpers('dashboard/dashboardDedicateTopMenu')

  export default {
    name: 'EventGrid',
    components: {DeviceStatusDataTable},
    props: {
      eventPopupCondition: {
        type: Boolean,
        default: true
      },
      contentData: [],
      tableName: {
        type: String,
        default: ''
      },
      tableData: {
        type: Array,
        required: true,
        default: []
      },
      height: {
        type: [Number, String]
      },
      tableColumn: {
        type: Array
      },
      slim: {
        type: Boolean,
        default: false
      },
      page: {
        type: Number,
        default: 1
      },
      pageSize: {
        type: Number,
        default: 10
      },
      totalDataCount: {
        type: Number,
        default: 0
      },
      paging: {
        type: Boolean,
        default: false
      },
      showPagination: {
        type: Boolean,
        default: false
      },
      showTotalCount: {
        type: Boolean,
        default: false
      },
      // 이벤트 레벨 정보 수동 갱신
      disableLoadEventLevelInfo: {
        type: Boolean,
        default: false
      },
      virtualScrolling: {
        type: Boolean,
        default: false
      },
      virtualScrollRefresh: {
        type: Boolean,
        default: false
      },
      pageSearchTextPlaceholder: {
        type: Boolean,
        default: false
      },
      disableSearchText: {
        type: Boolean,
        default: false
      },
      isPopup: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        fields: [],
        // tableHeight: 0,
        // 페이징
        pageIndex: 1,
        pageMin: 1,
        maxPages: 5,
        searchText: '',
        eventLevelFilters: {},
        defaultFields: [
          {
            name: 'event_level_name',
            field: 'event_level_name',
            align: 'center',
            label: this.$t('cmn.level'),
            width: 50,
            custom: { type: 'eventLevel' },
            tooltip: _.noop(),
            sorter: (data1, data2, item1, item2) => {
              try {
                return sortCharNum(item1.event_level_type, item2.event_level_type)
              } catch (e) {
                return 0
              }
            },
            headerType: [
              {
                type: 'button',
                props: {
                  size: 'xs',
                  align: 'center',
                  color: 'dark',
                  flat: 'flat',
                  class: 'bs bs-q-btn-xs bs-q-btn-flat-type bs-q-btn-filter',
                  iconCustomClass: 'bs-q-btn-icon bs-q-btn-icon-data-filter-b'
                }
              }
            ]
          },
          {
            name: 'event_log_dt',
            field: 'event_log_dt',
            align: 'center',
            label: this.$t('cmn.time')
          },
          {
            name: 'device_uname',
            field: 'device_uname',
            align: 'center',
            label: this.$t('cmn.deviceName')
          },
          {
            name: 'device_ip',
            field: 'device_ip',
            align: 'center',
            label: this.$t('cmn.ip'),
            sorter: (data1, data2, item1, item2) => {
              try {
                return ipSorter(item1.device_ip, item2.device_ip)
              } catch (e) {
                return 0
              }
            }
          },
          {
            name: 'device_grp_uname',
            field: 'device_grp_uname',
            align: 'center',
            width: 50,
            label: this.$t('cmn.deviceGroup')
          },
          {
            name: 'event_type_name',
            field: 'event_type_name',
            align: 'center',
            label: this.$t('lr.log_dataTable_event')
          },
          {
            name: 'event_name',
            field: 'event_name',
            align: 'center',
            label: this.$t('lr.log_dataTable_case')
          },
          {
            name: 'message',
            field: 'message',
            align: 'left',
            label: this.$t('cmn.msg'),
            width: 200
          }
        ]
      }
    },
    computed: {
      ...mapState([
        'eventLevelInfo'
      ]),
      ...deviceStatusHelper.mapState([
        'eventLogCnt' // 이벤트 로그 (최근 24시간)의 이벤트 갯수
      ]),
      ...mapStateTopMenu([
        'filterDevice'
      ]),
      totalPageCount () {
        // 이벤트 로그에서 검색 시 "페이지 내 검색"
        if (this.tableName === 'eventGrid24Table') return this.eventLogCnt
        else return this.filterTableData.length
      },
      pageMax () {
        // 이벤트 로그에서 검색 시 "페이지 내 검색"
        if (this.tableName === 'eventGrid24Table') return this.eventLogCnt === 0 ? 1 : Math.ceil(this.eventLogCnt / this.pageSize)
        else return this.filterTableData.length === 0 ? 1 : Math.ceil(this.filterTableData.length / this.pageSize)
      },
      getTableHeight () {
        return this.showPagination ? this.height - 28 : this.height
      },
      filterTableData () {
        try {
          const dataList = this.tableData
          let
            filterKey = this.searchText.toLowerCase(),
            data = []

          data = _.filter(dataList, (v) => {
            let flag = false
            _.each(this.fields, col => {
              const
                value = String(_.at(v, col.name)),
                compareWord = value.toLowerCase()

              if (compareWord.indexOf(filterKey) > -1) {
                flag = true
              }
            })

            return flag
          })

          return data
        } catch (e) {
          return []
        }
      }
    },
    methods: {
      ...mapActions([
        'getEventLevelData'
      ]),
      ...deviceStatusHelper.mapActions([
        'getEventLogData',
        'getEventLogData24',
        'getEventLogDataCnt'
      ]),
      ...deviceStatusHelper.mapMutations([
        'setEventLogDataFilter'
      ]),
      onAlramSettingPopup () {
        this.$emit('openPopup')
      },
      onInputPage () {
        if (this.paging) {
          // Client-side paging
          this.$refs.dataTable.openPage(this.pageIndex)
        } else {
          // Server-side paging
          this.$emit('searchPage', this.pageIndex)
        }

        // 최상단으로 스크롤 이동 설정
        // this.$refs.dataTable.jsGridExec('pageScrollToTop')

        // // 컬럼 소팅 설정 초기화
        // this.$refs.dataTable.jsGridExec('_resetSorting')
      },
      getConvertLevel (eventLevelName, row, field) {
        // jQuery 의존성때문에, cellRenderer 리턴값은 jQuery로 해야함
        // const { event_color: eventColor } = row
        let eventColor = ''
        try {
          eventColor = this.eventLevelInfo.find(info => info.event_level_type === row.event_level_type).event_level_color || ''
        } catch (e) {
          eventColor = this.eventLevelInfo[0].event_level_color
        }

        const backgroundColor = eventColor,
          textColor = getColorLuminance(backgroundColor)

        const $td = $(`<td>`)
        const $div = $(`<div>`)
        const $text = $(`<div>`)

        // $td.css('background', backgroundColor)
        // $td.addClass(`text-${textColor}`)

        $td.addClass('jsgrid-text-overflow')
        $td.addClass('no-padding') // TAMS, data-table-custom
        $td.attr('field-name', field) // cellClick 시 field-name 참고용 (필수)

        $div.css({
          'height': '100%',
          'background': backgroundColor,
          'color': textColor,
          'display': 'flex',
          'align-items': 'center',
          'justify-content': 'center'
        })
        $text.html(_.upperFirst(_.toLower(eventLevelName)))

        try {
          $td.hover(e => {
            const $textBox = $(e.currentTarget)
            renderTooltip($textBox, $div.text())
          }, e => {
            const $textBox = $(e.currentTarget)
            destroyTooltip($textBox)
          })
        } catch (e) {
          this.$log('[Error getConvertLevel]: ', e)
        }

        $div.append($text)
        $td.append($div)

        return $td
      },
      getConvertStartDate (item, row) {
        return Moment(item).format('YYYY-MM-DD HH:mm:ss') || item
      },
      scrollToTop () {
        this.$refs.dataTable.jsGridExec('pageScrollToTop')
      },
      scrollToLeft () {
        this.$refs.dataTable.jsGridExec('refreshScrollToLeft')
      },
      refresh () {
        this.$refs.dataTable.jsGridExec('refresh')
      },
      onResize () {
        // this.$refs.dataTable.onResize()
      },
      onVsResize () {
        if (typeof this.$refs.dataTable === 'undefined' || !this.virtualScrolling) {
          return
        }
        this.$nextTick(() => {
          this.render()
        })
      },
      render () {
      },
      onRowDoubleClick ({ row }) {
        try {
          this.$emit('rowDoubleClick', {item: row})
        } catch (e) {
          this.$log('Event Grid GUI Error (onRowDoubleClick)', e)
        }
      },
      resetPage () {
        try {
          this.pageIndex = 1
          // this.$refs.dataTable.render()
        } catch (e) {
          this.$log('Event Grid GUI Error (resetPage)', e)
        }
      },
      async setFilters () {
        this.pageIndex = 1
        this.$emit('eventLevelFilter')
      }
    },
    async mounted () {
      if (!this.disableLoadEventLevelInfo) {
        // 이벤트 레벨 정보 업데이트 (페이지 재접근 시 항상)
        await this.getEventLevelData()
      }

      // Init - Custom table colums settings
      if (typeof this.tableColumn !== 'undefined') {
        this.fields = this.tableColumn
      } else if (this.tableName === 'eventGridPopupTable') { // 미복구 이벤트 팝업창에서는 Level의 필터 비노출
        this.fields = _.cloneDeep(this.defaultFields)
        const idx = _.findIndex(this.fields, o => o.field === 'event_level_name')
        delete this.fields[idx].headerType
      } else {
        this.fields = this.defaultFields
      }
    },
    watch: {
      // pageIndex (val) {
      //   this.$emit('searchPage', val)
      // }
      page (nv, ov) {
        if (nv !== ov) {
          this.pageIndex = nv
          // 최상단으로 스크롤 이동 설정
          // this.$refs.dataTable.jsGridExec('pageScrollToTop')
        }
      }
    }
  }
</script>

<style scoped>
  .grid-wrapper {
    height: 100%;
  }
</style>
